import { useEffect, useState } from 'react';
import { isEmpty } from 'lodash-es';
import { all, map, path, pick, pipe, prop, props } from 'ramda';
import { useI18n } from '@namespace/i18n';
import { getEventsBySports } from '../../api';
import { sportsbookActions, useSportsbookStore } from '../../store';
import { useAdvFilterStore } from '../../storeAdvFilter';
import { useFetchEventsFilter } from '../useFetchEventsFilter';
import { getEventsRequestParams, IFilter } from './utils';

const { SET_EVENTS } = sportsbookActions;

type UseCustomFilterGroupsProps = {
  filter: Record<string, unknown>;
};

type Event = {
  eventId: number;
};

type GroupsData = {
  sportName: string;
  categoryName: string;
  lineKey: string;
  sportId: number;
  categoryId: number;
  tournamentIds: number[];
  sportWeight: number;
};

type RequestData = {
  by: {
    lang: string;
    head_markets: boolean;
    service_id: number;
  };
  offset: number;
};

type RequestParams = {
  requestData: RequestData;
  additionalData: GroupsData;
};

export const useCustomFilterGroups = ({
  filter
}: UseCustomFilterGroupsProps) => {
  const [groupsData, setGroupsData] = useState<
    Pick<
      GroupsData,
      | 'lineKey'
      | 'sportId'
      | 'sportName'
      | 'categoryId'
      | 'categoryName'
      | 'tournamentIds'
    >[]
  >([]);
  const { language: lang } = useI18n();
  const [isGroupsLoaded, setIsGroupsLoaded] = useState(false);
  const advFilter = useAdvFilterStore('advFilter');
  const advFilterSportsById = useAdvFilterStore('advFilterSportsById');
  const filterValue = useFetchEventsFilter(filter) as IFilter;

  useEffect(() => {
    if (!isEmpty(advFilter) && !isEmpty(advFilterSportsById)) {
      (async () => {
        try {
          const { lines } = useSportsbookStore.getState();
          const { advFilterCategoriesById } = useAdvFilterStore.getState();
          const sportsData = { advFilterSportsById, advFilterCategoriesById };

          const requestParams = getEventsRequestParams({
            customFiltersData: advFilter!,
            sportsData,
            lang,
            filterValue,
            lines
          });

          const advFilterLineKeys = map(
            path(['additionalData', 'lineKey']),
            requestParams
          );
          const isAllAdvFilterLinesCached = pipe(
            props(advFilterLineKeys as string[]),
            all(prop('isCached') as (data: unknown) => boolean)
          )(lines);

          if (!isAllAdvFilterLinesCached) {
            const { result: eventsGroups } = (await getEventsBySports({
              params: requestParams.map(({ requestData }) => requestData)
            })) as { result: Event[] };

            eventsGroups.forEach((eventGroup, index: number) => {
              const { requestData, additionalData } = requestParams[index];

              SET_EVENTS({
                dateFilterValue: undefined,
                events: eventGroup,
                lineKey: additionalData.lineKey,
                limit: requestData.limit,
                initialEventsLimit: requestData.limit
              });
            });
          }

          setIsGroupsLoaded(true);

          setGroupsData(
            map(
              pipe(
                (param: RequestParams) => prop('additionalData', param),
                (additionalData: GroupsData) =>
                  pick(
                    [
                      'lineKey',
                      'sportId',
                      'sportName',
                      'categoryId',
                      'categoryName',
                      'tournamentIds'
                    ],
                    additionalData
                  )
              )
            )(requestParams as unknown as RequestParams[])
          );
        } catch (e) {
          console.error(e);
        }
      })();
    }
  }, [advFilter, advFilterSportsById, filterValue, lang]);

  return {
    isGroupsLoaded,
    groupsData
  };
};
